import { useQuery } from "@tanstack/react-query";
import { ActionCard, KeyValue, StatusPill } from "@ozpanel/kit";
import { Section, StateView, useAuth } from "@ozpanel/ui";

/** GET /galera/rolling-restart — galera.handleRollingRestartStatus (rollingrestart.go). */
export interface RollingRestartStatus {
  node?: string;
  nodes?: {
    node: string;
    answered: boolean;
    pending?: string[] | null;
    pending_measured?: boolean;
    pending_error?: string;
    state?: string;
    cluster_status?: string;
    error?: string;
  }[];
  plan?: string[] | null;
  plan_error?: string;
  refused?: string;
  error?: string;
  hold?: { run: string; holder: string; node?: string; expires: string; expired: boolean };
  hold_error?: string;
  master_holder?: boolean;
  master_why?: string;
}

interface JobStep {
  name: string;
  status: string;
  log?: string;
  error?: string;
}

interface JobView {
  id: string;
  status: string;
  progress: number;
  current_step?: string;
  error?: string;
  steps?: JobStep[];
}

/** Why the server would refuse the run right now, or undefined when it would start. */
export function rollingRestartUnavailable(s: RollingRestartStatus | undefined): string | undefined {
  if (!s) return "The cluster's pending settings have not been read yet.";
  if (s.error) return `The cluster could not be described: ${s.error}`;
  if (s.plan_error) return `Refused: ${s.plan_error}`;
  if (!s.plan || s.plan.length === 0) return "No node has a restart-only setting pending.";
  if (s.hold && !s.hold.expired) return `A rolling restart already holds the cluster (run ${s.hold.run}, driven by ${s.hold.holder}).`;
  if (!s.master_holder) return `Only the master_holder drives this — open this page on that node (${s.master_why ?? "unknown"}).`;
  if (s.refused) return `Refused before ${s.plan[0]}: ${s.refused}`;
  return undefined;
}

function RollingJob({ id }: { id: string }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["galera-rolling-job", id],
    queryFn: () => client.apiFetch<JobView>(`/ozpanel/jobs/${encodeURIComponent(id)}`),
    refetchInterval: (qq) => {
      const s = qq.state.data?.status;
      return !s || s === "running" || s === "pending" ? 3000 : false;
    },
  });
  const j = q.data;
  return (
    <StateView loading={q.isLoading} error={q.error} errorContext="the rolling restart">
      <div className="space-y-1 text-[12px]">
        <div>
          <StatusPill tone={j?.status === "failed" ? "bad" : j?.status === "running" ? "warn" : "ok"}>{j?.status ?? "…"}</StatusPill>{" "}
          {j?.progress ?? 0}% {j?.current_step ? `— ${j.current_step}` : ""}
        </div>
        {j?.error && <div className="text-bad">{j.error}</div>}
        {(j?.steps ?? []).map((s) => (
          <div key={s.name}>
            <b>{s.name}</b>: {s.status}
            {s.log && <pre className="whitespace-pre-wrap text-muted">{s.log}</pre>}
            {s.error && <div className="text-bad">{s.error}</div>}
          </div>
        ))}
      </div>
    </StateView>
  );
}

/** The restart-only settings not yet in force on each node, and the rolling restart that applies them. */
export function GaleraRollingRestart() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["galera-rolling-restart"],
    queryFn: () => client.apiFetch<RollingRestartStatus>("/galera/rolling-restart"),
    refetchInterval: 15000,
  });
  const s = q.data;
  const unavailable = rollingRestartUnavailable(s);
  return (
    <Section title="Settings waiting for a restart" bodyClassName="p-0">
      <StateView loading={q.isLoading} error={q.error} errorContext="the pending restart">
        <KeyValue
          rows={(s?.nodes ?? []).map((n) => ({
            key: n.node,
            label: n.node,
            value: !n.answered
              ? `not asked: ${n.error ?? "no answer"}`
              : !n.pending_measured
                ? `unknown: ${n.pending_error ?? ""}`
                : (n.pending ?? []).length === 0
                  ? "nothing pending"
                  : (n.pending ?? []).join("; "),
            hint: n.answered ? `${n.cluster_status ?? "?"} · ${n.state ?? "?"}` : undefined,
          }))}
        />
        <div className="border-t border-line p-4">
          <ActionCard
            title="Apply the pending restart, one node at a time"
            description="Restarts MariaDB on each node that reports a pending setting, one at a time: before each node every data member must be Synced, the cluster size intact and the arbitrator present; the next node waits until this one is Synced again with the setting in force. Any failure stops the run and says where. Driven by the master_holder."
            actionLabel="Apply rolling restart"
            danger="high"
            unavailable={unavailable}
            preflight={(s?.nodes ?? []).map((n) => ({
              ok: n.answered && n.cluster_status === "Primary" && n.state === "Synced",
              label: `${n.node}: ${n.answered ? `${n.cluster_status ?? "?"} / ${n.state ?? "?"}` : "not asked"}`,
            }))}
            confirm={{
              title: "Restart MariaDB on every node with a pending setting?",
              body: `In this order: ${(s?.plan ?? []).join(" → ")}. Each node leaves the cluster for its restart and rejoins by state transfer.`,
              confirmLabel: "Restart rolling",
            }}
            run={() => client.apiFetch<{ job_id?: string }>("/galera/rolling-restart", { method: "POST" })}
            invalidates={[["galera-rolling-restart"], ["galera-wsrep"]]}
            renderResult={(r) => {
              const id = (r as { job_id?: string } | null)?.job_id;
              return id ? <RollingJob id={id} /> : <pre>{JSON.stringify(r, null, 2)}</pre>;
            }}
          />
        </div>
      </StateView>
    </Section>
  );
}
